import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Decode a JWT payload client-side (no signature verification — used only to
 * compare the *claimed* email of a stored session to a hint email from the
 * URL, so we can force a stale session to log out).
 */
function decodeJwtEmail(jwt) {
  try {
    if (!jwt) return null;
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(pad));
    return (json && typeof json.email === 'string') ? json.email.toLowerCase() : null;
  } catch (_) { return null; }
}

/**
 * Safe-redirect allow-list for the `?next=` parameter. Only internal paths
 * (same-origin, starting with a single '/') may be used as post-auth
 * destinations. Blocks `//evil.com`, `http(s)://…`, and any value that
 * `URL()` would resolve to a different origin.
 */
function safeNextPath(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null;
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return null;
    return u.pathname + u.search + u.hash;
  } catch (_) { return null; }
}

/** Extract a claim token from a `/claim/<token>` next path (or null). */
function claimTokenFromNext(next) {
  if (!next) return null;
  const m = next.match(/^\/claim\/([^/?#]+)/);
  return m ? m[1] : null;
}

export default function AuthPage({ setUser }) {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', name: '', role: 'creator' });
  const [loading, setLoading] = useState(false);
  const [nextPath, setNextPath] = useState(null);
  const [claimContext, setClaimContext] = useState(null); // { token, valid, publicPreview } | null

  // Parse the URL once on mount. Pulls out ?next= (sanitized), hint email,
  // and — if next points at /claim/<token> — probes the public claim peek
  // endpoint to decide whether to render the claim-aware onboarding shell.
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const hint = (params.get('email') || '').trim().toLowerCase();
        const n = safeNextPath(params.get('next'));
        setNextPath(n);

        if (hint) {
          const existing = decodeJwtEmail(localStorage.getItem('token'));
          if (existing && existing !== hint) {
            localStorage.removeItem('token');
            delete axios.defaults.headers.common['Authorization'];
            if (typeof setUser === 'function') setUser(null);
            toast.info(`Signed out of ${existing}. Please sign in as ${hint}.`);
          }
          setFormData((f) => (f.email ? f : { ...f, email: hint }));
        }

        const tok = claimTokenFromNext(n);
        if (tok) {
          try {
            const res = await axios.get(`${API}/claim/${encodeURIComponent(tok)}`);
            setClaimContext({ token: tok, valid: true, publicPreview: res.data || null });
            // Claim onboarding is always Holder onboarding — the server will
            // force the role regardless, but we align the form state so the
            // UI never shows a Creator/Architect role selector in this flow.
            setIsLogin(false);
            setFormData((f) => ({ ...f, role: 'buyer' }));
          } catch (_) {
            setClaimContext({ token: tok, valid: false, publicPreview: null });
          }
        }
      } catch (_) { /* ignore */ }
    })();
    // Intentionally mount-only: this effect reads from window.location.search
    // once and must not re-run on prop/state changes.
  }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      let payload;
      if (isLogin) {
        payload = { email: formData.email, password: formData.password };
      } else if (claimContext?.valid && claimContext.token) {
        // Claim-aware Holder signup — role is enforced server-side.
        payload = {
          email: formData.email, password: formData.password, name: formData.name,
          role: 'buyer', claim_token: claimContext.token,
        };
      } else {
        payload = { ...formData };
      }

      const response = await axios.post(`${API}${endpoint}`, payload);
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');

      // Post-auth redirect — only safe internal paths are honored.
      const dest = safeNextPath(nextPath);
      if (dest) {
        window.location.assign(dest);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const roleOptions = [
    { value: 'creator',   label: 'Creator' },
    { value: 'buyer',     label: 'Holder' },
    { value: 'architect', label: 'Kickback Architect' },
  ];

  const inClaimContext = !!(claimContext && claimContext.valid);

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #F8F9FB, #FFFFFF)' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 opacity-5 rounded-full blur-3xl" style={{ background: '#6C5CE7' }} />
        <div className="absolute bottom-20 right-20 w-96 h-96 opacity-5 rounded-full blur-3xl" style={{ background: '#39FF14' }} />
      </div>

      <div className="relative w-full max-w-md">
        <div className="p-8 fade-in" style={{ background: '#FFFFFF', borderRadius: '16px', boxShadow: '0 8px 24px rgba(14, 15, 18, 0.10), 0 4px 12px rgba(14, 15, 18, 0.08)', border: '1px solid #E6E8EF' }}>
          <div className="text-center mb-8">
            <h1 className="text-5xl font-bold mb-3" style={{ color: '#6C5CE7', fontFamily: 'Space Grotesk' }}>
              Kickback Contracts
            </h1>
            <p className="text-lg" style={{ color: '#0B0B0B' }}>Portfolio × Creator × Finance</p>
          </div>

          {inClaimContext && (
            <div data-testid="claim-context-banner" style={{ marginBottom: '20px', padding: '12px 14px', background: '#F3F0FF', border: '1px solid #D7CFF8', borderRadius: '10px', color: '#3B2E8C' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, marginBottom: 4 }}>
                {isLogin ? 'Sign in to claim this Kickback.' : 'Create your account to claim this Kickback.'}
              </div>
              <div style={{ fontSize: '12px', opacity: 0.85 }}>
                You'll return to the claim page as soon as you're signed in. Creating an account does not consume this claim.
              </div>
            </div>
          )}
          {claimContext && !claimContext.valid && (
            <div data-testid="claim-invalid-banner" style={{ marginBottom: '20px', padding: '12px 14px', background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: '10px', color: '#7F1D1D', fontSize: '13px' }}>
              This claim link is not currently valid. You can still sign in or create a Holder account below.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6 auth-form" data-testid="auth-form">
            <div className="space-y-2">
              <Label htmlFor="email" style={{ color: '#111', fontWeight: 600 }}>Email</Label>
              <Input id="email" type="email" placeholder="you@example.com"
                value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required data-testid="email-input"
                style={{ background: '#FFFFFF', border: '1px solid #D1D5DB', color: '#0B0B0B', padding: '12px', borderRadius: '8px', fontSize: '14px', width: '100%' }} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: '#111', fontWeight: 600 }}>Password</Label>
              <Input id="password" type="password" placeholder="Enter your password"
                value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required data-testid="password-input"
                style={{ background: '#FFFFFF', border: '1px solid #D1D5DB', color: '#0B0B0B', padding: '12px', borderRadius: '8px', fontSize: '14px', width: '100%' }} />
            </div>

            {!isLogin && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" style={{ color: '#111', fontWeight: 600 }}>Name</Label>
                  <Input id="name" type="text" placeholder="Your full name"
                    value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required data-testid="name-input"
                    style={{ background: '#FFFFFF', border: '1px solid #D1D5DB', color: '#0B0B0B', padding: '12px', borderRadius: '8px', fontSize: '14px', width: '100%' }} />
                </div>

                {!inClaimContext && (
                  <div className="space-y-2">
                    <Label htmlFor="role" style={{ color: '#111', fontWeight: 600 }}>I am a...</Label>
                    <select id="role" data-testid="role-select"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      style={{ background: '#FFFFFF', border: '1px solid #D1D5DB', color: '#0B0B0B', padding: '12px', borderRadius: '8px', fontSize: '14px', width: '100%' }}>
                      {roleOptions.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                      Choose Holder if you've received a Kickback claim link. Admin accounts are internal only.
                    </p>
                  </div>
                )}
              </>
            )}

            <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
              style={{ width: '100%', padding: '12px', background: '#6C5CE7', color: '#FFFFFF', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Processing...' : isLogin ? 'Sign in' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isLogin && (
              <a href="/auth/reset" data-testid="forgot-password-link"
                style={{ color: '#6C5CE7', textDecoration: 'underline', fontSize: '13px' }}>
                Forgot password?
              </a>
            )}
            <button data-testid="auth-toggle-mode" onClick={() => setIsLogin(!isLogin)}
              style={{ background: 'transparent', border: 'none', color: '#6C5CE7', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px' }}>
              {isLogin ? 'Need an account? Sign up' : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
